// Streamed global imagery for any body, as one bounded GPU atlas.
//
// Written for the Earth (NASA GIBS Landsat WELD) and generalised by body: the
// Moon streams the LRO WAC mosaic through the same code. A source describes
// its tile grid (equirectangular CRS84 tiles of `tileSize` pixels, `span(z)`
// degrees on a side), the level whose tiles form the page table, the finest
// level, and where tiles come from. Decoded images are discarded after a
// single-tile upload. The page table resolves missing pages to resident
// ancestors, and a sparse second-level table carries the levels finer than
// the page level.
//
// createPlanetImagery(source) returns the per-body API: imageryTile, tileAt,
// fetchImageryTile, selectImageryTiles, borderAncestors, fillImageryPages, imageryFade,
// updateImageryTransitions, `uniforms`, `glsl` (functions prefixed with the
// body name: earthImagery(uv, fallback), moonImagerySample(uv, fallback)...)
// and getStream(renderer).
import * as THREE from 'three';
import { bandUploadsOn, loadImage, uploadInBands } from '../bandupload.js';

export const IMAGERY_FADE_SECONDS = 1.2;
// Tiles that land within HOLD_SECONDS of each other start their fade
// together (see releaseHeld), so a view sharpens as a whole rather than one
// square at a time. A tile never waits longer than this for its company.
const HOLD_SECONDS = 0.9;
// Downloads in flight at once. Remote tiles take a few hundred milliseconds
// each, so this, not the network's bandwidth, set the pace at 4.
const CONCURRENCY = 10;
// While nothing is loading, the children of the view's most refined tiles are
// fetched into the browser cache (not the atlas), so a zoom in starts sharp.
const PREFETCH_CONCURRENCY = 2;
const FADE_DIRECTION_BIAS = IMAGERY_FADE_SECONDS + 1;
const DEG = Math.PI / 180;
const isTileResponse = response => response?.ok && /^image\//i.test(response.headers?.get('content-type') || '');

// source: {
//   name: 'earth',            GLSL/uniform prefix (earth -> uEarthAtlas, earthImagery)
//   radius: 6371000,          metres
//   tileSize: 512,            pixels per tile side
//   rootSpan: 288,            degrees per tile at z = 0 (span(z) = rootSpan / 2^z)
//   rootLevel: 3,             coarsest level selected
//   pageLevel: 7,             one page-table cell per tile of this level
//   maxLevel: 11,             finest level (2^(max - page) detail cells per page, <= 16)
//   atlasSide: 8,             atlas is atlasSide^2 tiles
//   maxBudget (optional):     the most leaves update() selects whatever budget a
//                             caller asks for, for an atlas made smaller (phones)
//   mask: true,               sample a water mask (uEarthOceanMask) and keep the fallback there
//   linear: false,            tiles hold data values (reflectance), not sRGB colour: skip the decode
//   channels: 4,              1 keeps only the red channel (an R8 atlas, a quarter of the memory)
//   bspline: false,           read through a cubic B-spline, for data a shader thresholds
//                             (bilinear kinks print the texel grid); tiles meet without a step
//   cacheName, localUrl(z, x, y), url(z, x, y), fetchRemote(tile, signal) (optional)
//   localMax (optional):      the finest level localUrl can serve; finer tiles go
//                             straight to url (or fetchRemote), localUrl still
//                             naming them in Cache Storage
//   available(z, x, y) (optional): false for a tile the set does not have (a
//                             sparse bundled pyramid), so it is never requested
//   emptyTexel (optional, 0..255): with available, the value every texel of a
//                             tile the set does not have would hold (the set
//                             leaves out empty ground). Those tiles then share
//                             one atlas slot of that value, so the page table
//                             covers their ground at the level the view asks
//                             for instead of leaving it to the fallback
//   concurrency: 10,          downloads in flight at once
//   composeFrom, compose(tile, signal) (optional): levels from composeFrom up are
//                             built by compose (a Response with an image) instead
//                             of fetched; the result is cached under localUrl
// }
export function createPlanetImagery(source) {
  const {
    name, radius: RADIUS, tileSize: TILE = 512, rootSpan, rootLevel, pageLevel, maxLevel,
    atlasSide: ATLAS_SIDE = 8, mask = false, linear = false, channels = 4, bspline = false,
    concurrency: MAX_IN_FLIGHT = CONCURRENCY, localMax = Infinity, gain = null, maxBudget = Infinity
  } = source;
  const P = name[0].toUpperCase() + name.slice(1);
  const PAGE_SPAN = rootSpan / 2 ** pageLevel;
  const PAGE_WIDTH = Math.round(360 / PAGE_SPAN), PAGE_HEIGHT = Math.round(180 / PAGE_SPAN);
  const DETAIL_CELLS = 2 ** (maxLevel - pageLevel);
  const DETAIL_SIDE = ATLAS_SIDE * DETAIL_CELLS;
  const ATLAS = TILE * ATLAS_SIDE;
  const ROOT_SPAN = rootSpan / 2 ** rootLevel;
  const ROOT_W = Math.round(360 / ROOT_SPAN), ROOT_H = Math.round(180 / ROOT_SPAN);
  if (DETAIL_CELLS > 16) throw new Error('Imagery detail levels exceed the sparse page table');

  function imageryTile(z, x, y) {
    const span = rootSpan / 2 ** z;
    const width = Math.round(360 / span), height = Math.round(180 / span);
    x = ((x % width) + width) % width;
    y = Math.max(0, Math.min(height - 1, y));
    return { z, x, y, span, key: `${z}/${x}/${y}`, localUrl: source.localUrl(z, x, y), url: source.url(z, x, y) };
  }

  function tileAt(lat, lon, z = pageLevel) {
    const span = rootSpan / 2 ** z;
    const longitude = ((lon + 180) % 360 + 360) % 360;
    return imageryTile(z, Math.floor(longitude / span), Math.floor((90 - lat) / span));
  }

  // Shipped/project tiles first; persistent browser storage covers imagery
  // explored on static hosting. GPU eviction never discards downloaded bytes.
  async function fetchImageryTile(tile, signal) {
    if (source.available && !source.available(tile.z, tile.x, tile.y)) return new Response(null, { status: 404, statusText: 'Not in the tile set' });
    let cache = null;
    try { if (globalThis.caches) cache = await caches.open(source.cacheName); } catch {}
    signal?.throwIfAborted();
    try { const cached = cache && await cache.match(tile.localUrl); if (isTileResponse(cached)) return cached; } catch {}
    let response;
    if (source.compose && tile.z >= source.composeFrom) response = await source.compose(tile, signal);
    else {
      if (tile.z <= localMax) try { response = await fetch(tile.localUrl, { signal }); } catch (error) { if (signal?.aborted) throw error; }
      if (!isTileResponse(response)) response = source.fetchRemote ? await source.fetchRemote(tile, signal) : await fetch(tile.url, { signal, mode: 'cors' });
    }
    if (isTileResponse(response) && cache && !signal?.aborted) {
      try { await cache.put(tile.localUrl, response.clone()); } catch {} // storage quota/private mode
    }
    return response;
  }

  // Conservative curved-tile bounds include the polar wedges and date line.
  // Refine the largest projected pages first, stopping at the memory budget.
  // shift(lat) (optional, radians to radians) is for imagery laid on the
  // ground displaced in longitude: the ground under map longitude lon at
  // latitude lat is at lon + shift(lat) (the clouds' wind drift).
  // levelCap (optional) stops refinement below maxLevel, for a caller that
  // must stay within the levels the project ships.
  function selectImageryTiles(camera, frustum, viewportHeight, fov, budget = 48, previous = [], shift = null, levelCap = maxLevel) {
    const topLevel = Math.min(maxLevel, levelCap);
    const refined = new Set();
    for (const tile of previous) for (let z = tile.z - 1; z >= rootLevel; z--) {
      const scale = 2 ** (tile.z - z);
      refined.add(`${z}/${Math.floor(tile.x / scale)}/${Math.floor(tile.y / scale)}`);
    }
    const scale = viewportHeight / (2 * Math.tan(fov * DEG / 2));
    const camLength = camera.length();
    const camNormal = camera.clone().normalize();
    const cameraLat = Math.asin(camNormal.y);
    const cameraLon = Math.atan2(-camNormal.z, camNormal.x);
    const horizon = Math.acos(Math.min(1, RADIUS / Math.max(RADIUS, camLength)));
    function candidate(tile) {
      const lat = (90 - (tile.y + 0.5) * tile.span) * DEG;
      let lon = (-180 + (tile.x + 0.5) * tile.span) * DEG;
      let angularRadius = tile.span * DEG * Math.SQRT1_2;
      let lean = 0;
      if (shift) {
        // A shift that varies with latitude shears the tile on the ground:
        // centre it on its middle and widen the bound by the most it leans.
        const s = shift(lat), h = tile.span * DEG * 0.5;
        const lats = [lat - h, lat + h];
        if (Math.abs(lat) < h) lats.push(0);
        for (const l of lats) lean = Math.max(lean, Math.abs(shift(l) - s));
        lean = Math.min(Math.PI, lean);
        lon += s;
        angularRadius += lean * Math.cos(Math.max(0, Math.abs(lat) - h));
      }
      const normal = new THREE.Vector3(Math.cos(lat) * Math.cos(lon), Math.sin(lat), -Math.cos(lat) * Math.sin(lon));
      if (normal.dot(camNormal) < Math.cos(Math.min(Math.PI, horizon + angularRadius))) return null;
      const centre = normal.multiplyScalar(RADIUS);
      const r = 2 * RADIUS * Math.sin(angularRadius / 2);
      if (frustum && !frustum.intersectsSphere(new THREE.Sphere(centre, r))) return null;
      // Sphere distance badly overestimates detail demand on grazing views:
      // large tile bounds can enclose a low camera even when all their ground
      // is hundreds of kilometres away. Measure to the geographic tile.
      const half = tile.span * DEG * 0.5;
      const longitudeDelta = Math.atan2(Math.sin(cameraLon - lon), Math.cos(cameraLon - lon));
      const nearLon = lon + THREE.MathUtils.clamp(longitudeDelta, -half - lean, half + lean);
      const nearLat = THREE.MathUtils.clamp(cameraLat, lat - half, lat + half);
      const nearest = new THREE.Vector3(Math.cos(nearLat) * Math.cos(nearLon), Math.sin(nearLat), -Math.cos(nearLat) * Math.sin(nearLon)).multiplyScalar(RADIUS);
      const distance = Math.max(100, nearest.distanceTo(camera));
      // Scores are in projected tile pixels of a 512 px tile.
      return { ...tile, score: RADIUS * tile.span * DEG * scale / distance * (512 / TILE) };
    }
    let leaves = [];
    for (let y = 0; y < ROOT_H; y++) for (let x = 0; x < ROOT_W; x++) {
      const tile = candidate(imageryTile(rootLevel, x, y));
      if (tile) leaves.push(tile);
    }
    const blocked = new Set();
    while (true) {
      leaves.sort((a, b) => b.score - a.score || a.key.localeCompare(b.key));
      const parent = leaves.find(t => t.z < topLevel && t.score > (refined.has(t.key) ? 420 : 620) && !blocked.has(t.key));
      if (!parent) break;
      const children = [];
      for (let y = 0; y < 2; y++) for (let x = 0; x < 2; x++) {
        const child = candidate(imageryTile(parent.z + 1, parent.x * 2 + x, parent.y * 2 + y));
        if (child) children.push(child);
      }
      if (leaves.length - 1 + children.length > budget) { blocked.add(parent.key); continue; }
      leaves = leaves.filter(t => t !== parent).concat(children);
    }
    return leaves.sort((a, b) => b.score - a.score || a.key.localeCompare(b.key)).slice(0, budget);
  }

  // Where a selected tile meets a coarser selected neighbour, the shader
  // hands the tile's border over to the coarser level (Border in the GLSL),
  // read from the resident ancestor the page table's parent bank holds under
  // the tile. These are the ancestors that border needs: for each edge with
  // a coarser neighbour, the tile's own ancestor at that neighbour's level,
  // the same level's imagery the neighbour shows across the edge. selected
  // is the set of the selection's keys.
  function borderAncestors(tile, selected) {
    const width = Math.round(360 / tile.span), height = Math.round(180 / tile.span);
    const ancestors = [];
    for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
      const x = (tile.x + dx + width) % width, y = tile.y + dy;
      if (y < 0 || y >= height) continue;
      for (let z = tile.z - 1; z >= rootLevel; z--) {
        const scale = 2 ** (tile.z - z);
        if (!selected.has(`${z}/${Math.floor(x / scale)}/${Math.floor(y / scale)}`)) continue;
        ancestors.push(imageryTile(z, Math.floor(tile.x / scale), Math.floor(tile.y / scale)));
        break;
      }
    }
    return ancestors;
  }

  function fillImageryPages(data, residents, selection = null, detailData = null) {
    data.fill(0);
    detailData?.fill(0);
    const rootLength = PAGE_WIDTH * PAGE_HEIGHT * 4;
    const detailLength = DETAIL_SIDE * DETAIL_SIDE * 4;
    const parents = data.length >= rootLength * 2 ? data.subarray(rootLength) : null;
    const detailParents = detailData?.length >= detailLength * 2 ? detailData.subarray(detailLength) : null;
    const records = [...residents].sort((a, b) => a.z - b.z);
    const requested = selection ? [...selection] : records;
    const levels = new Uint8Array(PAGE_WIDTH * PAGE_HEIGHT);
    const branches = new Map();
    for (const tile of requested) {
      const cells = 2 ** (pageLevel - tile.z);
      for (let y = Math.floor(tile.y * cells); y < Math.ceil((tile.y + 1) * cells); y++) {
        for (let x = Math.floor(tile.x * cells); x < Math.ceil((tile.x + 1) * cells); x++) {
          const i = y * PAGE_WIDTH + x;
          levels[i] = Math.max(levels[i], tile.z);
        }
      }
    }
    // Allocate sparse blocks only for resident detail, not unloaded requests.
    // Retained transition pages can then share the same bounded blocks.
    if (detailData) for (const tile of records) if (tile.z > pageLevel) {
      const cells = 2 ** (pageLevel - tile.z);
      const i = Math.floor(tile.y * cells) * PAGE_WIDTH + Math.floor(tile.x * cells);
      if ((!selection || tile.z <= levels[i]) && !branches.has(i)) branches.set(i, branches.size);
    }
    // Resident coarse ancestors remain underneath missing detail pages.
    for (const tile of records) {
      if (tile.z > pageLevel) continue;
      const cells = 2 ** (pageLevel - tile.z);
      for (let y = tile.y * cells; y < (tile.y + 1) * cells; y++) {
        for (let x = tile.x * cells; x < (tile.x + 1) * cells; x++) {
          if (selection && tile.z > levels[y * PAGE_WIDTH + x]) continue;
          const offset = (y * PAGE_WIDTH + x) * 4;
          if (parents && !((tile.fadeStamp ?? tile.loadedAt) < 0 && data[offset + 3] < 0 && parents[offset + 2] > 0)) parents.set(data.subarray(offset, offset + 4), offset);
          data.set([tile.slot % ATLAS_SIDE, Math.floor(tile.slot / ATLAS_SIDE), cells, tile.fadeStamp ?? tile.loadedAt], offset);
        }
      }
    }
    if (!detailData) return data;
    if (branches.size > ATLAS_SIDE ** 2) throw new Error('Imagery detail page budget exceeded');
    const detailLevels = new Uint8Array(DETAIL_SIDE * DETAIL_SIDE);
    function detailRegion(tile, visit) {
      const scale = 2 ** (maxLevel - tile.z);
      const gx = tile.x * scale, gy = tile.y * scale;
      const rootX = Math.floor(gx / DETAIL_CELLS), rootY = Math.floor(gy / DETAIL_CELLS);
      const branch = branches.get(rootY * PAGE_WIDTH + rootX);
      if (branch === undefined) return;
      const sx = branch % ATLAS_SIDE * DETAIL_CELLS, sy = Math.floor(branch / ATLAS_SIDE) * DETAIL_CELLS;
      for (let y = gy % DETAIL_CELLS; y < gy % DETAIL_CELLS + scale; y++) {
        for (let x = gx % DETAIL_CELLS; x < gx % DETAIL_CELLS + scale; x++) visit((sy + y) * DETAIL_SIDE + sx + x);
      }
    }
    for (const tile of requested) if (tile.z > pageLevel) detailRegion(tile, i => { detailLevels[i] = Math.max(detailLevels[i], tile.z); });
    for (const [root, branch] of branches) {
      const parent = data.slice(root * 4, root * 4 + 4);
      const sx = branch % ATLAS_SIDE * DETAIL_CELLS, sy = Math.floor(branch / ATLAS_SIDE) * DETAIL_CELLS;
      for (let y = sy; y < sy + DETAIL_CELLS; y++) for (let x = sx; x < sx + DETAIL_CELLS; x++) {
        const offset = (y * DETAIL_SIDE + x) * 4;
        detailData.set(parent, offset);
        if (detailParents && parents) detailParents.set(parents.subarray(root * 4, root * 4 + 4), offset);
      }
      // Keep the root's coarse image as the base for an intermediate parent
      // that is itself still fading (a rapid second refinement).
      if (parents) parents.set(parent, root * 4);
      // A negative scale redirects the root cell into the sparse table.
      data.set([sx, sy, -1, 0], root * 4);
    }
    for (const tile of records) if (tile.z > pageLevel) detailRegion(tile, i => {
      if (tile.z <= detailLevels[i]) {
        if (detailParents && !((tile.fadeStamp ?? tile.loadedAt) < 0 && detailData[i * 4 + 3] < 0 && detailParents[i * 4 + 2] > 0)) detailParents.set(detailData.subarray(i * 4, i * 4 + 4), i * 4);
        detailData.set([tile.slot % ATLAS_SIDE, Math.floor(tile.slot / ATLAS_SIDE), 2 ** (pageLevel - tile.z), tile.fadeStamp ?? tile.loadedAt], i * 4);
      }
    });
    return data;
  }

  const uniforms = {
    [`u${P}VolumeBlend`]: { value: 0 },
    [`u${P}Atlas`]: { value: null },
    [`u${P}Pages`]: { value: null },
    [`u${P}DetailPages`]: { value: null },
    [`u${P}ImageryOn`]: { value: 0 },
    [`u${P}ImageryTime`]: { value: 0 }
  };
  if (mask) uniforms[`u${P}OceanMask`] = { value: null };

  // A correction multiplied into every tile as it is drawn into the atlas
  // (gain: { url }, a plate carree map of gains per channel in linear light,
  // stored as 0.5 + log2(gain) / 2): the Earth's scene-seam gain
  // (scripts/build-earth-imagery-gain.mjs). It depends on the place alone,
  // so every level takes the same correction and the main shaders need no
  // texture unit for it (the globe's has none to spare). Uploads wait for it
  // (GAIN_WAIT_SECONDS at most) so the atlas never mixes corrected tiles with
  // uncorrected ones; once given up on, it is not applied for the session.
  const GAIN_WAIT_SECONDS = 12;
  const gainState = { texture: null, settled: !gain || typeof document === 'undefined', since: typeof performance !== 'undefined' ? performance.now() : 0 };
  if (!gainState.settled) {
    const loaded = (tex) => {
      if (gainState.settled) { tex.dispose(); return; }
      tex.colorSpace = THREE.NoColorSpace;
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.ClampToEdgeWrapping;
      tex.minFilter = tex.magFilter = THREE.LinearFilter;
      tex.generateMipmaps = false;
      // In the viewer it goes to the GPU in bands, ahead of any map waiting
      // (src/bandupload.js): sent whole on the first tile's draw it held
      // that frame 90 ms. The wait for it (GAIN_WAIT_SECONDS) does not run
      // out while it goes up.
      gainState.sending = true;
      uploadInBands(null, tex, gain.url, tex.image, { first: true }).then(() => {
        gainState.sending = false;
        if (gainState.settled) { tex.dispose(); return; }
        gainState.texture = tex;
        gainState.settled = true;
      });
    };
    const failed = (err) => {
      console.warn(`[imagery] ${name} gain unavailable:`, err?.message || err);
      gainState.settled = true;
    };
    if (bandUploadsOn()) {
      loadImage(gain.url).then((image) => {
        const tex = new THREE.Texture(image);
        tex.needsUpdate = true;
        loaded(tex);
      }, failed);
    } else {
      new THREE.TextureLoader().load(gain.url, loaded, undefined, failed);
    }
  }
  // window.__imageryGainOff = true (before the page loads) draws every tile
  // without it: before/after captures, and the tests, which cannot load it.
  const gainOff = () => Boolean(globalThis.__imageryGainOff);
  function gainReady() {
    if (gainOff()) return true;
    if (!gainState.settled && !gainState.sending && performance.now() - gainState.since > GAIN_WAIT_SECONDS * 1000) {
      console.warn(`[imagery] ${name} gain still loading after ${GAIN_WAIT_SECONDS} s: drawing tiles without it`);
      gainState.settled = true;
    }
    return gainState.settled;
  }

  const f = (v) => v.toFixed(1);
  const p = name;
  const glsl = /* glsl */`
uniform float u${P}VolumeBlend;
uniform sampler2D u${P}Atlas;
uniform sampler2D u${P}Pages;
${DETAIL_CELLS > 1 ? `uniform sampler2D u${P}DetailPages;` : '// No level finer than the page level: no detail table (one texture unit fewer).'}
${mask ? `uniform sampler2D u${P}OceanMask;` : ''}
uniform float u${P}ImageryOn;
uniform float u${P}ImageryTime;
vec3 ${p}PageSample(vec2 slot, vec2 p, vec2 dx, vec2 dy) {
  // Mips filter minified orbital imagery before sampling. Restrict the
  // footprint to this tile so the atlas cannot leak neighbouring pages.
  float footprint = max(1.0, max(length(dx), length(dy)));
  float k = min(1.0, 64.0 / footprint);
  dx *= k; dy *= k;
  float margin = min(64.0, exp2(ceil(log2(footprint * k)))) * 0.5;
  vec2 uv = (slot * ${f(TILE)} + clamp(p, vec2(margin), vec2(${f(TILE)} - margin))) / ${f(ATLAS)};
  vec3 srgb = textureGrad(u${P}Atlas, uv, dx / ${f(ATLAS)}, dy / ${f(ATLAS)}).rgb;
  ${linear ? 'return srgb;' : 'return mix(srgb / 12.92, pow((srgb + 0.055) / 1.055, vec3(2.4)), step(vec3(0.04045), srgb));'}
}
float ${p}PageFade(vec4 entry) {
  float t = entry.w < 0.0 ? 1.0 - (u${P}ImageryTime + entry.w + ${FADE_DIRECTION_BIAS}) / ${IMAGERY_FADE_SECONDS}
                         : (u${P}ImageryTime - entry.w) / ${IMAGERY_FADE_SECONDS};
  return smoothstep(0.0, 1.0, t);
}
vec2 ${p}PagePosition(vec2 page) {
  return vec2(mod(mod(page.x, ${f(PAGE_WIDTH)}) + ${f(PAGE_WIDTH)}, ${f(PAGE_WIDTH)}), clamp(page.y, 0.000001, ${f(PAGE_HEIGHT)} - 0.0001));
}
vec4 ${p}Entry(vec2 page) {
  page = ${p}PagePosition(page);
  vec4 entry = texture2D(u${P}Pages, (floor(page) + 0.5) / vec2(${f(PAGE_WIDTH)}, ${f(PAGE_HEIGHT * 2)}));
  ${DETAIL_CELLS > 1 ? `if (entry.z < 0.0) entry = texture2D(u${P}DetailPages, (entry.xy + floor(fract(page) * ${f(DETAIL_CELLS)}) + 0.5) / vec2(${f(DETAIL_SIDE)}, ${f(DETAIL_SIDE * 2)}));` : ''}
  return entry;
}
vec4 ${p}Parent(vec2 page) {
  vec2 uv = (floor(page) + 0.5) / vec2(${f(PAGE_WIDTH)}, ${f(PAGE_HEIGHT * 2)});
  vec4 root = texture2D(u${P}Pages, uv);
  ${DETAIL_CELLS > 1 ? `if (root.z < 0.0) return texture2D(u${P}DetailPages, (root.xy + floor(fract(page) * ${f(DETAIL_CELLS)}) + 0.5) / vec2(${f(DETAIL_SIDE)}, ${f(DETAIL_SIDE * 2)}) + vec2(0.0, 0.5));` : ''}
  return texture2D(u${P}Pages, uv + vec2(0.0, 0.5));
}
${bspline ? `// One texel of the tile at entry (origin its corner in pages), by texel
// coordinates in atlas orientation. Outside the tile it is read from the
// neighbouring tile of the same level where that is resident, so a filter
// straddling the border sees the ground's own texels there; past the poles,
// or with no such neighbour, the edge is held.
vec3 ${p}TileTexel(vec4 entry, vec2 origin, vec2 t) {
  vec2 inside = clamp(t, vec2(0.0), vec2(${f(TILE - 1)}));
  vec2 slot = entry.xy;
  if (t != inside) {
    vec2 q = origin + vec2(t.x + 0.5, ${f(TILE)} - t.y - 0.5) * (entry.z / ${f(TILE)});
    if (q.y > 0.0 && q.y < ${f(PAGE_HEIGHT)}) {
      vec2 qw = ${p}PagePosition(q);
      vec4 other = ${p}Entry(qw);
      if (other.z != entry.z) other = ${p}Parent(qw);
      if (other.z == entry.z) { slot = other.xy; inside = mod(t, ${f(TILE)}); }
    }
  }
  ${linear ? `return textureLod(u${P}Atlas, (slot * ${f(TILE)} + inside + 0.5) / ${f(ATLAS)}, 0.0).rgb;` : `// Colour tiles decode here as PageSample does, or the 16-tap border path
  // reads brighter than the interior's (a light line along every tile edge).
  vec3 srgb = textureLod(u${P}Atlas, (slot * ${f(TILE)} + inside + 0.5) / ${f(ATLAS)}, 0.0).rgb;
  return mix(srgb / 12.92, pow((srgb + 0.055) / 1.055, vec3(2.4)), step(vec3(0.04045), srgb));`}
}
` : ''}vec3 ${p}FilteredPage(vec4 entry, vec2 page, vec2 pageDx, vec2 pageDy) {
  vec2 p = fract(page / entry.z) * ${f(TILE)};
  p.y = ${f(TILE)} - p.y;
  vec2 dx = pageDx * (${f(TILE)} / entry.z) * vec2(1.0, -1.0);
  vec2 dy = pageDy * (${f(TILE)} / entry.z) * vec2(1.0, -1.0);
  ${bspline ? `// A cubic B-spline at every footprint: smooth to the second derivative,
  // never outside the texels' range and the texels' own mean, so a
  // threshold on it draws no texel grid. Four bilinear taps, each through
  // the mips the footprint picks: magnified it is the spline itself, and
  // as the pixel grows the mips take over from it, so a change of view only
  // ever low-passes the data (there is no second filter to hand over to).
  // Magnified within two texels of the tile's edge, sixteen texels are read
  // across the border, so neighbouring tiles meet without a step.
  vec2 st = p - 0.5, i = floor(st), t = st - i;
  vec2 w0 = (1.0 - t) * (1.0 - t) * (1.0 - t) / 6.0, w1 = (4.0 - 6.0 * t * t + 3.0 * t * t * t) / 6.0;
  vec2 w2 = (1.0 + 3.0 * t + 3.0 * t * t - 3.0 * t * t * t) / 6.0, w3 = t * t * t / 6.0;
  bool interior = min(i.x, i.y) >= 1.0 && max(i.x, i.y) <= ${f(TILE - 3)};
  if (interior || max(length(dx), length(dy)) > 1.0) {
    vec2 g0 = w0 + w1, g1 = w2 + w3;
    vec2 h0 = i - 0.5 + w1 / g0, h1 = i + 1.5 + w3 / g1;
    return g0.y * (g0.x * ${p}PageSample(entry.xy, h0, dx, dy) + g1.x * ${p}PageSample(entry.xy, vec2(h1.x, h0.y), dx, dy)) +
      g1.y * (g0.x * ${p}PageSample(entry.xy, vec2(h0.x, h1.y), dx, dy) + g1.x * ${p}PageSample(entry.xy, h1, dx, dy));
  }
  vec2 origin = floor(page / entry.z) * entry.z;
  vec3 cubic = vec3(0.0);
  for (int b = 0; b < 4; b++) {
    float wy = b == 0 ? w0.y : (b == 1 ? w1.y : (b == 2 ? w2.y : w3.y));
    vec3 row = vec3(0.0);
    for (int a = 0; a < 4; a++) {
      float wx = a == 0 ? w0.x : (a == 1 ? w1.x : (a == 2 ? w2.x : w3.x));
      row += wx * ${p}TileTexel(entry, origin, i + vec2(float(a) - 1.0, float(b) - 1.0));
    }
    cubic += wy * row;
  }
  return cubic;` : `return ${p}PageSample(entry.xy, p, dx, dy);`}
}
float ${p}Valid(vec3 colour) {
  return smoothstep(0.0001, 0.002, max(colour.r, max(colour.g, colour.b)));
}
// One border of a tile of cells pages, toward point just across it; ramp is
// 0 on the border and 1 a feather's width in. A neighbour of the same level
// or finer that is still fading in, or missing, feathers the tile into its
// base at the neighbour's own opacity, so neighbours arriving at different
// times share their edge. A coarser neighbour is a level boundary that can
// stay for as long as the view does: there the tile hands its border over
// to the coarser level's imagery of its own ground (handover, that level's
// tile size in handoverCells), which is what the neighbour shows across it.
void ${p}Border(vec2 point, float ramp, float cells, inout float feather, inout float handover, inout float handoverCells) {
  vec4 other = ${p}Entry(point);
  if (other.z > cells) {
    handover = min(handover, ramp);
    handoverCells = max(handoverCells, other.z);
  } else feather = min(feather, mix(other.z > 0.0 ? ${p}PageFade(other) : 0.0, 1.0, ramp));
}
// The ground one imagery texel spans, in degrees, per page of tile size:
// a sample's texel is (cells / alpha) times this (cells below).
const float ${P.toUpperCase()}_IMAGERY_TEXEL_DEG = ${(PAGE_SPAN / TILE).toPrecision(8)};
// Alpha reports how much resident imagery contributed. Terrain uses it to
// retire a local layer only after its global replacement is ready. This
// form takes the footprint in pages per pixel, so it can be called inside a
// branch; cells returns the tile size in pages (1 at the page level, doubling
// per coarser level) mixed by the same weights as the colour, premultiplied.
// landWeight scales the imagery's share (the water mask's land, or 1).
vec4 ${p}ImageryCore(vec2 mapUV, vec3 fallback, vec2 pageDx, vec2 pageDy, float landWeight, out float cells) {
  cells = 0.0;
  vec2 page = ${p}PagePosition(vec2(mapUV.x * ${f(PAGE_WIDTH)}, (1.0 - mapUV.y) * ${f(PAGE_HEIGHT)}));
  if (u${P}ImageryOn < 0.5 || landWeight <= 0.0) return vec4(fallback, 0.0);
  vec4 entry = ${p}Entry(page);
  if (entry.z <= 0.0) return vec4(fallback, 0.0);
  vec2 tileUV = fract(page / entry.z);
  vec2 tileOrigin = floor(page / entry.z) * entry.z;
  float feather = 1.0, handover = 1.0, handoverCells = entry.z;
  const float border = 0.10;
  // Only boundary pixels need neighbour lookups. Fully loaded neighbours
  // remove the feather entirely, so there is no permanent blurry tile grid.
  if (tileUV.x < border) ${p}Border(vec2(tileOrigin.x - 0.0001, page.y), smoothstep(0.0, border, tileUV.x), entry.z, feather, handover, handoverCells);
  if (tileUV.x > 1.0 - border) ${p}Border(vec2(tileOrigin.x + entry.z + 0.0001, page.y), smoothstep(0.0, border, 1.0 - tileUV.x), entry.z, feather, handover, handoverCells);
  if (tileUV.y < border) ${p}Border(vec2(page.x, tileOrigin.y - 0.0001), smoothstep(0.0, border, tileUV.y), entry.z, feather, handover, handoverCells);
  if (tileUV.y > 1.0 - border) ${p}Border(vec2(page.x, tileOrigin.y + entry.z + 0.0001), smoothstep(0.0, border, 1.0 - tileUV.y), entry.z, feather, handover, handoverCells);
  vec3 colour = ${p}FilteredPage(entry, page, pageDx, pageDy);
  float weight = ${p}PageFade(entry) * feather * ${p}Valid(colour);
  vec3 base = fallback;
  float baseWeight = 0.0;
  float baseCells = 0.0;
  if (weight < 0.9999 || handover < 0.9999) {
    vec4 parent = ${p}Parent(page);
    if (parent.z > 0.0) {
      vec3 parentColour = ${p}FilteredPage(parent, page, pageDx, pageDy);
      if (${p}PageFade(parent) < 0.9999) {
        vec4 coarse = texture2D(u${P}Pages, (floor(page) + 0.5) / vec2(${f(PAGE_WIDTH)}, ${f(PAGE_HEIGHT * 2)}) + vec2(0.0, 0.5));
        if (coarse.z > parent.z) {
          vec3 coarseColour = ${p}FilteredPage(coarse, page, pageDx, pageDy);
          baseWeight = ${p}PageFade(coarse) * ${p}Valid(coarseColour);
          base = mix(base, coarseColour, baseWeight);
          baseCells = coarse.z * baseWeight;
        }
      }
      float parentWeight = ${p}PageFade(parent) * ${p}Valid(parentColour);
      base = mix(base, parentColour, parentWeight);
      baseWeight = mix(baseWeight, 1.0, parentWeight);
      baseCells = mix(baseCells, parent.z, parentWeight);
    }
  }
  // The tile's own share, handed over along a coarser neighbour's border to
  // that level's imagery (its tile size, premultiplied, in tileCells and
  // its imagery share in tileShare).
  float tileCells = entry.z, tileShare = 1.0;
  if (handover < 0.9999) {
    // That level's imagery here is the resident ancestor in base. Where none
    // is resident (still on its way, or past the spare atlas slots) the
    // tile's own texels stand in, read at that level's resolution (a
    // footprint of at least one of its texels), so the border never shows
    // the fallback while the tile itself is resident.
    vec3 level = base;
    float levelCells = baseCells, levelShare = baseWeight;
    if (baseWeight < 0.9999) {
      float footprint = max(length(pageDx), length(pageDy)) * ${f(TILE)} / handoverCells;
      float widen = max(1.0, 1.0 / max(footprint, 1e-6));
      vec3 own = ${p}FilteredPage(entry, page, pageDx * widen, pageDy * widen);
      float ownWeight = (1.0 - baseWeight) * ${p}Valid(own);
      level += ownWeight * (own - fallback);
      levelCells += ownWeight * handoverCells;
      levelShare += ownWeight;
    }
    colour = mix(level, colour, handover);
    tileCells = mix(levelCells, entry.z, handover);
    tileShare = mix(levelShare, 1.0, handover);
  }
  cells = landWeight * mix(baseCells, tileCells, weight);
  // Preserve the fallback (ocean colour on Earth) for both paths.
  return vec4(mix(fallback, mix(base, colour, weight), landWeight),
    landWeight * mix(baseWeight, tileShare, weight));
}
vec4 ${p}ImagerySampleGrad(vec2 mapUV, vec3 fallback, vec2 pageDx, vec2 pageDy, out float cells) {
  ${mask ? `vec2 geographicUV = vec2(fract(mapUV.x), clamp(mapUV.y, 0.0, 1.0));
  float water = texture2D(u${P}OceanMask, geographicUV).r;
  float landWeight = 1.0 - smoothstep(0.0, 0.5, water);` : 'float landWeight = 1.0;'}
  return ${p}ImageryCore(mapUV, fallback, pageDx, pageDy, landWeight, cells);
}
// The imagery over land and water alike, for a caller that tells the two
// apart itself (the globe reads the coast from the imagery up close). The
// footprint is in pages per pixel: the screen derivatives of
// ${p}ImageryPage(mapUV), taken where control flow is uniform, so this can
// run in a branch and with a widened footprint.
vec2 ${p}ImageryPage(vec2 mapUV) {
  return vec2(mapUV.x * ${f(PAGE_WIDTH)}, (1.0 - mapUV.y) * ${f(PAGE_HEIGHT)});
}
vec4 ${p}ImageryOpen(vec2 mapUV, vec3 fallback, vec2 pageDx, vec2 pageDy, out float cells) {
  return ${p}ImageryCore(mapUV, fallback, pageDx, pageDy, 1.0, cells);
}
vec4 ${p}ImagerySample(vec2 mapUV, vec3 fallback) {
  // Compute footprint derivatives before any divergent tile/mask branches,
  // from the unwrapped page so an address running continuously across the
  // date line keeps its footprint there.
  vec2 raw = vec2(mapUV.x * ${f(PAGE_WIDTH)}, (1.0 - mapUV.y) * ${f(PAGE_HEIGHT)});
  vec2 pageDx = dFdx(raw), pageDy = dFdy(raw);
  float cells;
  return ${p}ImagerySampleGrad(mapUV, fallback, pageDx, pageDy, cells);
}
vec3 ${p}Imagery(vec2 mapUV, vec3 fallback) {
  return ${p}ImagerySample(mapUV, fallback).rgb;
}
`;

  // A signed timestamp encodes both directions without updating textures every
  // frame. Reverse an unfinished fade at its current opacity, never at 0 or 1.
  function imageryFade(tile, now) {
    const stamp = tile.fadeStamp ?? tile.loadedAt;
    return THREE.MathUtils.clamp(stamp < 0 ? 1 - (now + stamp + FADE_DIRECTION_BIAS) / IMAGERY_FADE_SECONDS : (now - stamp) / IMAGERY_FADE_SECONDS, 0, 1);
  }

  // One atlas per renderer, shared by the viewer and rocketry. Mode switches
  // transfer its consumer rather than allocating a second atlas or losing pages.
  const streams = new WeakMap();
  function getStream(renderer) {
    if (streams.has(renderer)) return streams.get(renderer);
    const stream = createStream(renderer);
    streams.set(renderer, stream);
    return stream;
  }

  function createStream(renderer) {
    const atlas = new THREE.WebGLRenderTarget(ATLAS, ATLAS, {
      depthBuffer: false, stencilBuffer: false, minFilter: THREE.LinearMipmapLinearFilter,
      magFilter: THREE.LinearFilter, generateMipmaps: true,
      ...(channels === 1 ? { format: THREE.RedFormat } : {})
    });
    const pages = new THREE.DataTexture(new Float32Array(PAGE_WIDTH * PAGE_HEIGHT * 8), PAGE_WIDTH, PAGE_HEIGHT * 2, THREE.RGBAFormat, THREE.FloatType);
    pages.needsUpdate = true;
    const detailPages = new THREE.DataTexture(new Float32Array(DETAIL_SIDE * DETAIL_SIDE * 8), DETAIL_SIDE, DETAIL_SIDE * 2, THREE.RGBAFormat, THREE.FloatType);
    detailPages.needsUpdate = true;
    uniforms[`u${P}DetailPages`].value = detailPages;
    uniforms[`u${P}Atlas`].value = atlas.texture;
    uniforms[`u${P}Pages`].value = pages;
    // The tile's bitmap is decoded flipped (vUv.y 0 is its southern edge);
    // box is its corner and size in the gain map's uv (v north up).
    const copy = new THREE.ShaderMaterial({
      uniforms: { source: { value: null }, gainMap: { value: null }, gainOn: { value: 0 }, box: { value: new THREE.Vector4() } },
      depthTest: false, depthWrite: false, toneMapped: false,
      vertexShader: 'varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }',
      fragmentShader: `uniform sampler2D source; uniform sampler2D gainMap; uniform float gainOn; uniform vec4 box; varying vec2 vUv;
        void main() {
          vec4 c = texture2D(source, vUv);
          if (gainOn > 0.5) {
            vec3 g = exp2((texture2D(gainMap, box.xy + vUv * box.zw).rgb - 0.5) * 2.0);
            vec3 lin = mix(c.rgb / 12.92, pow((c.rgb + 0.055) / 1.055, vec3(2.4)), step(vec3(0.04045), c.rgb)) * g;
            lin = clamp(lin, 0.0, 1.0);
            c.rgb = mix(lin * 12.92, 1.055 * pow(lin, vec3(1.0 / 2.4)) - 0.055, step(vec3(0.0031308), lin));
          }
          gl_FragColor = c;
        }`
    });
    const scene = new THREE.Scene();
    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), copy);
    quad.frustumCulled = false;
    scene.add(quad);
    const copyCamera = new THREE.Camera();
    const residents = new Map(), pending = new Map(), failed = new Map();
    // The shared slot of a sparse set's missing tiles (source.emptyTexel), the
    // atlas's last, filled on first use.
    const EMPTY_SLOT = source.available && source.emptyTexel !== undefined ? ATLAS_SIDE ** 2 - 1 : -1;
    let emptyFilled = false, emptyOn = true;
    let selection = [], wanted = new Map(), ready = [], generation = 0, lastSelect = -Infinity, disposed = false;
    let levelCap = maxLevel;
    // Uploaded tiles waiting to fade in together, and the idle prefetch.
    let heldSince = null, prefetchWanted = [];
    const prefetching = new Set(), prefetched = new Set();
    const camera = new THREE.Vector3();
    const view = new THREE.Matrix4(), projection = new THREE.Matrix4(), frustum = new THREE.Frustum();
    const size = new THREE.Vector2();
    const start = performance.now();
    const stats = { loaded: 0, failed: 0, pending: 0, resident: 0, selected: 0, maxLevel: 0, ancestors: 0, ancestorsDropped: 0,
      // The atlas with its mip chain, in bytes.
      atlasBytes: Math.round(ATLAS * ATLAS * (channels === 1 ? 1 : 4) * 4 / 3) };

    // The atlas freed and its pages forgotten, for a phone well away from the
    // body (its callers): the shader falls back to the body's own map, and
    // the next update draws the view's tiles again from Cache Storage.
    let released = false;
    function release() {
      if (released) return;
      cancel();
      residents.clear();
      emptyFilled = false;
      rebuildPages();
      atlas.dispose();
      stats.resident = 0;
      released = true;
    }

    function cancel() {
      generation++;
      for (const controller of pending.values()) controller.abort();
      pending.clear();
      for (const entry of ready) entry.bitmap.close();
      ready = [];
      wanted.clear(); selection = []; prefetchWanted = [];
      for (const tile of residents.values()) if (tile.held) { tile.held = false; tile.fadeStamp = 0; }
      heldSince = null;
      stats.pending = stats.selected = stats.maxLevel = stats.ancestors = stats.ancestorsDropped = 0;
      lastSelect = -Infinity;
      uniforms[`u${P}ImageryOn`].value = 0;
    }

    function requestTiles(now) {
      for (const tile of wanted.values()) {
        if (pending.size + ready.length >= MAX_IN_FLIGHT) break;
        if (residents.has(tile.key) || pending.has(tile.key) || ready.some(t => t.key === tile.key) || (failed.get(tile.key) || 0) > now) continue;
        const controller = new AbortController(), epoch = generation;
        pending.set(tile.key, controller);
        const timeout = setTimeout(() => controller.abort(), 12000);
        fetchImageryTile(tile, controller.signal)
          .then(response => { if (!response.ok) throw new Error(`Imagery ${response.status}`); return response.blob(); })
          .then(blob => createImageBitmap(blob, { imageOrientation: 'flipY', premultiplyAlpha: 'none', colorSpaceConversion: 'none' }))
          .then(bitmap => {
            if (disposed || epoch !== generation || !wanted.has(tile.key)) { bitmap.close(); return; }
            ready.push({ ...tile, bitmap });
          })
          .catch(() => {
            if (epoch !== generation || !wanted.has(tile.key)) return;
            failed.set(tile.key, performance.now() + 60000);
            stats.failed++;
          })
          .finally(() => {
            clearTimeout(timeout);
            if (pending.get(tile.key) === controller) pending.delete(tile.key);
          });
      }
    }

    function rebuildPages() {
      const displayed = [...residents.values()].filter(t => t.display !== false);
      fillImageryPages(pages.image.data, displayed, [...wanted.values(), ...displayed], detailPages.image.data);
      pages.needsUpdate = detailPages.needsUpdate = true;
    }

    // Draws texture over atlas slot slot; with a tile, through the gain.
    function drawSlot(texture, slot, tile = null) {
      copy.uniforms.source.value = texture;
      // A GPU that cannot hold the map (old phones stop at 4096) draws every
      // tile without it, so the atlas still agrees with itself.
      const g = tile && !linear && !gainOff() && gainState.texture && gainState.texture.image.width <= renderer.capabilities.maxTextureSize ? gainState.texture : null;
      copy.uniforms.gainMap.value = g;
      copy.uniforms.gainOn.value = g ? 1 : 0;
      if (g) copy.uniforms.box.value.set((tile.x * tile.span) / 360, (90 - (tile.y + 1) * tile.span + 90) / 180, tile.span / 360, tile.span / 180);
      const target = renderer.getRenderTarget(), face = renderer.getActiveCubeFace(), mip = renderer.getActiveMipmapLevel();
      const autoClear = renderer.autoClear;
      const xr = renderer.xr.enabled;
      try {
        renderer.xr.enabled = false;
        renderer.autoClear = false;
        // Target viewports are physical pixels. renderer.setViewport applies
        // the display pixel ratio, which would corrupt the atlas on Retina.
        atlas.viewport.set(slot % ATLAS_SIDE * TILE, Math.floor(slot / ATLAS_SIDE) * TILE, TILE, TILE);
        renderer.setRenderTarget(atlas);
        renderer.render(scene, copyCamera);
        released = false;
      } finally {
        renderer.setRenderTarget(target, face, mip);
        renderer.autoClear = autoClear; renderer.xr.enabled = xr;
        copy.uniforms.source.value = null;
        copy.uniforms.gainMap.value = null;
      }
    }

    // The missing tiles the view asks for, as residents of the empty slot
    // (EMPTY_SLOT): no download, and they fade in as a tile would.
    function addEmptyTiles(seconds) {
      if (EMPTY_SLOT < 0 || !emptyOn) return;
      for (const tile of wanted.values()) {
        if (residents.has(tile.key) || source.available(tile.z, tile.x, tile.y)) continue;
        if (!emptyFilled) {
          const v = source.emptyTexel;
          const texture = new THREE.DataTexture(new Uint8Array([v, v, v, 255]), 1, 1);
          texture.needsUpdate = true;
          drawSlot(texture, EMPTY_SLOT);
          texture.dispose();
          emptyFilled = true;
        }
        residents.set(tile.key, { ...tile, slot: EMPTY_SLOT, empty: true, loadedAt: seconds, fadeStamp: seconds, display: true, lastUsed: seconds });
      }
    }

    function upload(now) {
      if (!ready.length || !gainReady()) return;
      const tile = ready.shift();
      if (!wanted.has(tile.key)) { tile.bitmap.close(); return; }
      let slot = 0;
      const used = new Set([...residents.values()].map(t => t.slot));
      if (EMPTY_SLOT >= 0) used.add(EMPTY_SLOT);
      while (used.has(slot)) slot++;
      if (slot >= ATLAS_SIDE ** 2) {
        // The empty slot is shared and never anyone's to give up.
        const victim = [...residents.values()].filter(t => !t.empty && !wanted.has(t.key) && t.display === false).sort((a, b) => a.lastUsed - b.lastUsed)[0];
        if (!victim) {
          // Never overwrite a still-visible atlas slot. Under pressure, retire
          // one old page smoothly, then reuse it after its fade has finished.
          if (![...residents.values()].some(t => t.retire && t.display !== false)) {
            const retire = [...residents.values()].filter(t => !t.empty && !wanted.has(t.key)).sort((a, b) => a.lastUsed - b.lastUsed)[0];
            if (retire) { retire.retire = true; fadeToward(retire, false, now); rebuildPages(); }
          }
          ready.unshift(tile);
          return;
        }
        slot = victim.slot;
        residents.delete(victim.key);
      }
      const texture = new THREE.Texture(tile.bitmap);
      texture.flipY = false;
      texture.generateMipmaps = false;
      texture.minFilter = texture.magFilter = THREE.LinearFilter;
      texture.needsUpdate = true;
      try {
        drawSlot(texture, slot, tile);
      } finally {
        texture.dispose(); tile.bitmap.close();
      }
      const { bitmap, ...record } = tile;
      // Held: resident and displayed, but its fade waits (a stamp in the
      // future reads as fully transparent) until releaseHeld starts it.
      residents.set(tile.key, { ...record, slot, loadedAt: now, fadeStamp: now + 1e6, held: true, display: true, lastUsed: now });
      if (heldSince === null) heldSince = now;
      updateImageryTransitions(residents, selection, wanted, now);
      rebuildPages();
      stats.loaded++;
    }

    // Start the held tiles' fades together once everything asked for has
    // arrived, or once the first of them has waited HOLD_SECONDS.
    function releaseHeld(now) {
      if (heldSince === null) return false;
      const settled = pending.size === 0 && ready.length === 0;
      if (!settled && now - heldSince < HOLD_SECONDS) return false;
      // A tile that stopped being wanted while it waited is already fading
      // out (its stamp went negative) and stays that way.
      for (const tile of residents.values()) if (tile.held) { tile.held = false; if (tile.fadeStamp > now) tile.fadeStamp = now; }
      heldSince = null;
      return true;
    }

    // The children of the most refined tiles that are close to refining,
    // nearest the centre of attention first.
    function planPrefetch() {
      prefetchWanted = [];
      // Composed levels cost a dozen or more requests each, so they are
      // built only when the view actually asks for them.
      const prefetchMax = Math.min(levelCap, source.compose ? Math.min(maxLevel, source.composeFrom - 1) : maxLevel);
      for (const tile of selection) {
        if (tile.z >= prefetchMax || tile.score < 300) continue;
        for (let y = 0; y < 2; y++) for (let x = 0; x < 2; x++) {
          const child = imageryTile(tile.z + 1, tile.x * 2 + x, tile.y * 2 + y);
          if (!residents.has(child.key) && !wanted.has(child.key) && !prefetched.has(child.key)) prefetchWanted.push(child);
        }
        if (prefetchWanted.length >= 16) break;
      }
    }

    function runPrefetch() {
      if (pending.size || ready.length) return;
      while (prefetching.size < PREFETCH_CONCURRENCY && prefetchWanted.length) {
        const tile = prefetchWanted.shift();
        if (prefetched.has(tile.key) || prefetching.has(tile.key)) continue;
        prefetching.add(tile.key);
        prefetched.add(tile.key);
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 12000);
        fetchImageryTile(tile, controller.signal)
          .then(response => response?.body?.cancel?.())
          .catch(() => prefetched.delete(tile.key))
          .finally(() => { clearTimeout(timeout); prefetching.delete(tile.key); });
      }
    }

    // prefetch (1 or more) widens the selection's frustum by that factor in
    // the lens's own tangent space, leaving each tile's detail score as the
    // real lens gives it, so the tiles just past the edge of the view are
    // requested before a pan or the orbit brings them on screen.
    function update({ now, position, orientation, lens, oceanMask, budget = 48, shift = null, prefetch = 1, maxLevel: cap = maxLevel }) {
      levelCap = cap;
      budget = Math.min(budget, maxBudget);
      const seconds = (now - start) / 1000;
      if (mask) uniforms[`u${P}OceanMask`].value = oceanMask || null;
      uniforms[`u${P}ImageryTime`].value = seconds;
      uniforms[`u${P}ImageryOn`].value = mask ? (oceanMask?.image ? 1 : 0) : 1;
      if (now - lastSelect > 150) {
        lastSelect = now;
        camera.copy(position);
        view.compose(camera, orientation, new THREE.Vector3(1, 1, 1)).invert();
        // The lens is dimensionless. Its clipping range is replaced in metres.
        projection.copy(lens);
        projection.elements[10] = -(1e10 + 1) / (1e10 - 1);
        projection.elements[14] = -2e10 / (1e10 - 1);
        const fov = 2 * Math.atan(1 / projection.elements[5]) / DEG;
        if (prefetch > 1) { projection.elements[0] /= prefetch; projection.elements[5] /= prefetch; }
        frustum.setFromProjectionMatrix(projection.multiply(view));
        renderer.getDrawingBufferSize(size);
        selection = selectImageryTiles(camera, frustum, size.y, fov, budget, selection, shift, levelCap);
        // The spare atlas slots hold ancestors, fetched before the selection
        // and kept resident while it needs them: the page-level parent of
        // each detail tile, broad coverage on a cold view that a slow or
        // failed request leaves standing, and the ancestors each tile's
        // border hands over to where it meets a coarser neighbour
        // (borderAncestors), so that border shows that level's imagery for as
        // long as the boundary stays. When they outnumber the spare slots
        // the selection's order decides, largest on screen first.
        const parents = new Map(), spare = ATLAS_SIDE ** 2 - budget;
        const selected = new Set(selection.map(t => t.key));
        const dropped = new Set();
        const keep = tile => {
          if (parents.has(tile.key)) return;
          if (parents.size < spare) parents.set(tile.key, tile); else dropped.add(tile.key);
        };
        for (const tile of selection) {
          if (tile.z > pageLevel) {
            const scale = 2 ** (tile.z - pageLevel);
            keep(imageryTile(pageLevel, Math.floor(tile.x / scale), Math.floor(tile.y / scale)));
          }
          for (const ancestor of borderAncestors(tile, selected)) keep(ancestor);
        }
        stats.ancestors = parents.size; stats.ancestorsDropped = dropped.size;
        wanted = new Map([...parents.values(), ...selection].map(t => [t.key, t]));
        for (const [key, controller] of pending) if (!wanted.has(key)) { controller.abort(); pending.delete(key); }
        for (const [key, tile] of residents) if (wanted.has(key)) tile.lastUsed = seconds;
        for (const [key, until] of failed) if (until <= now) failed.delete(key);
        addEmptyTiles(seconds);
        updateImageryTransitions(residents, selection, wanted, seconds);
        // An empty tile out of view holds no slot of its own: drop it.
        for (const [key, tile] of residents) if (tile.empty && tile.display === false) residents.delete(key);
        rebuildPages();
        stats.selected = selection.length;
        stats.maxLevel = Math.max(0, ...selection.map(t => t.z));
        planPrefetch();
      }
      if (updateImageryTransitions(residents, selection, wanted, seconds) | releaseHeld(seconds)) rebuildPages();
      upload(seconds); // At most one tile GPU upload per frame.
      requestTiles(now);
      runPrefetch();
      stats.pending = pending.size; stats.resident = residents.size;
      stats.held = heldSince === null ? 0 : [...residents.values()].filter(t => t.held).length;
      stats.prefetched = prefetched.size;
    }
    // Whether missing tiles take the empty slot (on unless a caller wants the
    // fallback there instead, for before and after captures).
    function setEmptyTiles(on) {
      if (on === emptyOn) return;
      emptyOn = on;
      if (!on) for (const [key, tile] of residents) if (tile.empty) residents.delete(key);
      lastSelect = -Infinity;
    }
    // gainTexture: the gain map once on hand, for checks.
    const api = { stats, update, cancel, release, setEmptyTiles, get gainTexture() { return gainState.texture; }, dispose() {
      disposed = true; cancel(); streams.delete(renderer);
      atlas.dispose(); pages.dispose(); detailPages.dispose(); copy.dispose(); quad.geometry.dispose();
      uniforms[`u${P}Atlas`].value = uniforms[`u${P}Pages`].value = null;
      if (mask) uniforms[`u${P}OceanMask`].value = null;
      uniforms[`u${P}DetailPages`].value = null;
    } };
    return api;
  }

  return {
    TILE_SIZE: TILE, ATLAS_SIDE, PAGE_WIDTH, PAGE_HEIGHT, PAGE_SPAN, MAX_LEVEL: maxLevel, PAGE_LEVEL: pageLevel, DETAIL_SIDE,
    imageryTile, tileAt, fetchImageryTile, selectImageryTiles, borderAncestors, fillImageryPages, imageryFade,
    updateImageryTransitions, uniforms, glsl, getStream
  };
}

function containsTile(parent, child) {
  if (parent.z > child.z) return false;
  const scale = 2 ** (child.z - parent.z);
  return parent.x === Math.floor(child.x / scale) && parent.y === Math.floor(child.y / scale);
}

function imageryFadeAt(tile, now) {
  const stamp = tile.fadeStamp ?? tile.loadedAt;
  return THREE.MathUtils.clamp(stamp < 0 ? 1 - (now + stamp + FADE_DIRECTION_BIAS) / IMAGERY_FADE_SECONDS : (now - stamp) / IMAGERY_FADE_SECONDS, 0, 1);
}

function fadeToward(tile, visible, now) {
  const stamp = tile.fadeStamp ?? tile.loadedAt;
  if ((stamp >= 0) === visible) return false;
  const alpha = imageryFadeAt(tile, now);
  tile.fadeStamp = visible ? now - alpha * IMAGERY_FADE_SECONDS : -(now - (1 - alpha) * IMAGERY_FADE_SECONDS + FADE_DIRECTION_BIAS);
  return true;
}

export function updateImageryTransitions(residents, selection, wanted, now) {
  let changed = false;
  for (const tile of residents.values()) {
    const wasDisplayed = tile.display !== false;
    if (wanted.has(tile.key)) {
      tile.retire = false;
      if (!wasDisplayed) tile.fadeStamp = now;
      changed = fadeToward(tile, true, now) || changed;
      tile.display = true;
    } else {
      const overlap = selection.filter(t => containsTile(t, tile) || containsTile(tile, t));
      if (!overlap.length) tile.display = false;
      else {
        const ancestor = overlap.find(t => containsTile(t, tile));
        const replacementReady = ancestor && residents.has(ancestor.key) && imageryFadeAt(residents.get(ancestor.key), now) >= 1;
        const childrenReady = !ancestor && overlap.every(t => residents.has(t.key) && imageryFadeAt(residents.get(t.key), now) >= 1);
        if (tile.retire || replacementReady || childrenReady) {
          changed = fadeToward(tile, false, now) || changed;
          tile.display = imageryFadeAt(tile, now) > 0;
        } else if (!wasDisplayed) { tile.display = true; tile.fadeStamp = now; }
        else changed = fadeToward(tile, true, now) || changed;
        // Otherwise retain coverage until the replacement actually arrives.
      }
    }
    changed = (wasDisplayed !== (tile.display !== false)) || changed;
  }
  return changed;
}
